import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../utils/supabaseClient';
import Header from '../../header/header';
import styles from './adminPageMatch.module.css';
import {User} from "@supabase/supabase-js";
import SubstituteForm, {SubstituteData} from '../substituteForm/substituteForm';

interface AdminPageProps {
    user: User
}
interface Player {
    id: number;
    first_name: string;
    last_name: string;
    number: number;
    in_match_squad: boolean;
    from_start: boolean;
    captain: boolean;
    team_id: number;
}

interface MatchEvent {
    id: number;
    fk_event_type: number;
    fk_persona: number;
    time: number;
    opt_add_time: number | null;
    description: string | null;
    fk_parent: number | null;
    period2match: {
        id: number;
        fk_period: {
            id: number;
            period: string;
        }
    }
}

interface EventType {
    id: number;
    event_type: string;
    icon: string;
}

interface Period {
    id: number;
    fk_period: {
        id: number;
        period: string;
    }[];
}

interface Match {
    id: number;
    name: string;
    status: string;
    type: string;
    actual_start_time: string;
    fk_team_host: number;
    fk_team_guest: number;
    host_team_name: { name: string };
    guest_team_name: { name: string };
    fk_tournament: number;
}

const adminPageMenu = [
    { name: 'Cities & counties', link: '/admin/geo' },
    { name: 'News', link: '/admin/news' },
    { name: 'Teams', link: '/admin/teams' },
    { name: 'Matches', link: '/admin/matches' }
];

const AdminPageMatch: React.FC<AdminPageProps> = () => {
    const { matchId } = useParams<{ matchId: string }>();
    const navigate = useNavigate();
    const [match, setMatch] = useState<Match | null>(null);
    const [homeTeamPlayers, setHomeTeamPlayers] = useState<Player[]>([]);
    const [awayTeamPlayers, setAwayTeamPlayers] = useState<Player[]>([]);
    const [matchEvents, setMatchEvents] = useState<MatchEvent[]>([]);
    const [eventTypes, setEventTypes] = useState<EventType[]>([]);
    const [matchPeriods, setMatchPeriods] = useState<Period[]>([]);
    const [allPeriodTypes, setAllPeriodTypes] = useState<{ id: number; period: string }[]>([]);
    const [selectedPeriodType, setSelectedPeriodType] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [newEventType, setNewEventType] = useState('');
    const [newEventTime, setNewEventTime] = useState('');
    const [newEventPlayer, setNewEventPlayer] = useState('');
    const [newEventAddTime, setNewEventAddTime] = useState('');
    const [newEventDescription, setNewEventDescription] = useState('');
    const [newEventPeriod, setNewEventPeriod] = useState('');
    const [newEventParent, setNewEventParent] = useState('');

    useEffect(() => {
        if (matchId) {
            const loadData = async () => {
                try {
                    await fetchMatchDetails(parseInt(matchId));
                    const events = await fetchMatchEvents(parseInt(matchId));
                    setMatchEvents(events);
                } catch (error) {
                    console.error('Error loading match data:', error);
                    setError('Failed to load match data');
                }
            };
            loadData();
            fetchEventTypes();
            fetchAllPeriodTypes();
        }
    }, [matchId]);

    const fetchMatchDetails = async (id: number) => {
        setIsLoading(true);
        setError(null);
        try {
            const { data: matchData, error: matchError } = await supabase
                .from('matches')
                .select(`*,
                host_team_name: teams!fk_team_host (name),
                guest_team_name: teams!fk_team_guest (name)`)
                .eq('id', id)
                .single();

            if (matchError) throw matchError;
            if (!matchData) throw new Error('Match not found');

            setMatch(matchData);

            // Fetch match periods
            const { data: periodsData, error: periodsError } = await supabase
                .from('period2match')
                .select('id, fk_period(id, period)')
                .eq('fk_match', id);

            if (periodsError) throw periodsError;
            const processedPeriodsData = periodsData.map(period => ({
                ...period,
                fk_period: Array.isArray(period.fk_period) ? period.fk_period : [period.fk_period].filter(Boolean),
            }));
            setMatchPeriods(processedPeriodsData);

            // Fetch match events
            const periodIds = periodsData.map(period => period.id);
            const { data: eventData, error: eventError } = await supabase
                .from('match_events')
                .select(`
                    *,
                    period2match!match_events_fk_period2match_fkey(
                        id,
                        fk_period(id, period)
                    )
                `)
                .in('fk_period2match', periodIds)
                .order('time', { ascending: true });

            if (eventError) throw eventError;
            setMatchEvents(eventData || []);

            // Fetch team players
            const homePlayers = await fetchTeamPlayers(matchData.fk_team_host, id);
            const awayPlayers = await fetchTeamPlayers(matchData.fk_team_guest, id);

            const validHomePlayers = homePlayers.filter((player): player is Player => player !== null);
            const validAwayPlayers = awayPlayers.filter((player): player is Player => player !== null);

            setHomeTeamPlayers(validHomePlayers);
            setAwayTeamPlayers(validAwayPlayers);

        } catch (err) {
            setError('Error fetching match details. Please try again.');
            console.error('Error fetching match details:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchTeamPlayers = async (teamId: number, matchId: number) => {
        try {
            // Сначала получаем информацию о матче и его турнире
            const { data: matchData, error: matchDataError } = await supabase
                .from('matches')
                .select(`
                id,
                fk_tournament,
                tournaments (
                    id,
                    type,
                    fk_parent
                )
            `)
                .eq('id', matchId)
                .single();

            if (matchDataError) throw matchDataError;
            console.log(matchData)

            // Находим родительский турнир типа "Tournament"
            let tournamentId = matchData.fk_tournament;
            let currentTournament = Array.isArray(matchData.tournaments)
                ? matchData.tournaments[0]
                : matchData.tournaments; // Берем первый элемент массива

            console.log('Initial tournament:', currentTournament);

            while (currentTournament && currentTournament.type !== 'Tournament') {
                console.log('Current tournament:', currentTournament);

                if (!currentTournament.fk_parent) {
                    console.log('No parent tournament found, breaking the loop');
                    break;
                }

                const { data: parentData, error: parentError } = await supabase
                    .from('tournaments')
                    .select('id, type, fk_parent')
                    .eq('id', currentTournament.fk_parent)
                    .single();

                if (parentError) {
                    console.error('Error fetching parent tournament:', parentError);
                    throw parentError;
                }

                if (!parentData) {
                    console.log('No parent data found, breaking the loop');
                    break;
                }

                console.log('Parent tournament found:', parentData);

                tournamentId = parentData.id;
                currentTournament = parentData;
            }

            console.log('Final tournament ID:', tournamentId);

            // Теперь получаем игроков для этого турнира
            const { data: tournamentSquad, error: tournamentError } = await supabase
                .from('persona2team2season2tournament')
                .select(`
                                    id,
                                    persona2team2season!inner (
                                        id,
                                        persona2team!inner (
                                            id,
                                            fk_team,
                                            number,
                                            personas!inner (
                                                id,
                                                first_name,
                                                last_name
                                            )
                                        )
                                    )
                                `)
                .eq('persona2team2season.persona2team.fk_team', teamId)
                .eq('fk_tournament', tournamentId);
            console.log (tournamentSquad)

            if (tournamentError) {
                console.error('Error fetching tournament squad:', tournamentError);
                throw tournamentError;
            }

            console.log('Raw tournament squad data:', tournamentSquad);

            // Fetch match squad
            const { data: matchSquad, error: matchError } = await supabase
                .from('persona2team2season2tournament2match')
                .select(`
                id,
                from_start,
                captain,
                fk_persona2team2season2tournament (
                    id,
                    persona2team2season (
                        persona2team (
                            number,
                            personas (
                                id
                            )
                        )
                    )
                )
            `)
                .eq('fk_match', matchId);

            if (matchError) throw matchError;

            // Combine the data
            const players = tournamentSquad.map(player => {
                console.log('Processing player:', player);

                const persona2team2season = Array.isArray(player.persona2team2season)
                    ? player.persona2team2season[0]
                    : player.persona2team2season;

                const persona2team = Array.isArray(persona2team2season?.persona2team)
                    ? persona2team2season.persona2team[0]
                    : persona2team2season?.persona2team;

                const persona = Array.isArray(persona2team?.personas)
                    ? persona2team.personas[0]
                    : persona2team?.personas;

                if (!persona) {
                    console.log('No persona data for player:', player);
                    return null;
                }

                const matchPlayer = matchSquad.find((mp) => {
                    const p2t2s2t = Array.isArray(mp.fk_persona2team2season2tournament)
                        ? mp.fk_persona2team2season2tournament[0] as any
                        : mp.fk_persona2team2season2tournament as any;

                    if (p2t2s2t) {
                        const p2t2s = Array.isArray(p2t2s2t.persona2team2season)
                            ? p2t2s2t.persona2team2season[0] as any
                            : p2t2s2t.persona2team2season as any;

                        if (p2t2s) {
                            const p2t = Array.isArray(p2t2s.persona2team)
                                ? p2t2s.persona2team[0] as any
                                : p2t2s.persona2team as any;

                            if (p2t) {
                                const p = Array.isArray(p2t.personas)
                                    ? p2t.personas[0] as any
                                    : p2t.personas as any;

                                return p?.id === persona.id;
                            }
                        }
                    }
                    return false;
                });

                return {
                    id: persona.id,
                    first_name: persona.first_name,
                    last_name: persona.last_name,
                    in_match_squad: !!matchPlayer,
                    from_start: matchPlayer?.from_start || false,
                    captain: matchPlayer?.captain || false,
                    number: persona2team.number || null,
                    team_id: persona2team.fk_team,
                };
            }).filter(player => player !== null);

            console.log('Final players list:', players);
            return players;

        } catch (error) {
            console.error('Error fetching team players:', error);
            throw error;
        }
    }

    const fetchEventTypes = async () => {
        try {
            const { data, error } = await supabase
                .from('match_event_types')
                .select('*');
            if (error) throw error;
            setEventTypes(data || []);
        } catch (err) {
            console.error('Error fetching event types:', err);
            setError('Failed to load event types.');
        }
    };

    const fetchAllPeriodTypes = async () => {
        try {
            const { data, error } = await supabase
                .from('match_periods')
                .select('id, period')
                .order('id', { ascending: true });

            if (error) throw error;
            setAllPeriodTypes(data);
        } catch (err) {
            console.error('Error fetching period types:', err);
            setError('Failed to load period types.');
        }
    };

    const handleUpdateMatchStatus = async (newStatus: string) => {
        setIsLoading(true);
        setError(null);
        try {
            const { error } = await supabase
                .from('matches')
                .update({ status: newStatus })
                .eq('id', match?.id);
            if (error) throw error;
            setMatch(match => match ? {...match, status: newStatus} : null);
        } catch (err) {
            setError('Failed to update match status. Please try again.');
            console.error('Error updating match status:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const updatePlayerStatus = async (player: Player, team: 'home' | 'away', status: 'in_match_squad' | 'from_start' | 'captain') => {
        if (!match) return;

        const teamId = team === 'home' ? match.fk_team_host : match.fk_team_guest;

        try {
            console.log('Updating player status:', { player, team, status, teamId, matchId: match.id });

            // Найдем соответствующую запись в persona2team2season2tournament
            const { data: p2t2s2tData, error: p2t2s2tError } = await supabase
                .from('persona2team2season2tournament')
                .select(`
                id,
                fk_tournament,
                persona2team2season!inner (
                    id,
                    persona2team!inner (
                        id,
                        fk_team,
                        number,
                        personas!inner (
                            id
                        )
                    )
                )
            `)
                .eq('persona2team2season.persona2team.personas.id', player.id)
                .eq('persona2team2season.persona2team.fk_team', teamId)
                .eq('persona2team2season.persona2team.number', player.number)
            console.log('p2t2s2tData:', p2t2s2tData);

            if (p2t2s2tError) {
                console.error('Error fetching persona2team2season2tournament:', p2t2s2tError);
                throw p2t2s2tError;
            }

            if (!p2t2s2tData || p2t2s2tData.length === 0) {
                console.error('No matching persona2team2season2tournament found');
                return;
            }

            const p2t2s2t = p2t2s2tData[0];  // Берем первый элемент, если их несколько

            if (status === 'in_match_squad') {
                if (player.in_match_squad) {
                    // Remove from match squad
                    const { error: deleteError } = await supabase
                        .from('persona2team2season2tournament2match')
                        .delete()
                        .eq('fk_persona2team2season2tournament', p2t2s2t.id)
                        .eq('fk_match', match.id);

                    if (deleteError) {
                        console.error('Error removing player from match squad:', deleteError);
                        throw deleteError;
                    }
                } else {
                    // Add to match squad
                    const { error: insertError } = await supabase
                        .from('persona2team2season2tournament2match')
                        .insert({
                            fk_persona2team2season2tournament: p2t2s2t.id,
                            fk_match: match.id,
                            from_start: false,
                            captain: false
                        });

                    if (insertError) {
                        console.error('Error adding player to match squad:', insertError);
                        throw insertError;
                    }
                }
            } else {
                // Update from_start or captain status
                const { error: updateError } = await supabase
                    .from('persona2team2season2tournament2match')
                    .update({ [status]: !player[status] })
                    .eq('fk_persona2team2season2tournament', p2t2s2t.id)
                    .eq('fk_match', match.id);

                if (updateError) {
                    console.error('Error updating player status:', updateError);
                    throw updateError;
                }
            }

            // Refresh player data
            const updatedPlayers = await fetchTeamPlayers(teamId, match.id);
            const validUpdPlayers = updatedPlayers.filter((player): player is Player => player !== null);
// Теперь можно безопасно использовать `validPlayers`, так как это точно `Player[]`

            if (team === 'home') {
                setHomeTeamPlayers(validUpdPlayers);
            } else {
                setAwayTeamPlayers(validUpdPlayers);
            }
        } catch (error) {
            console.error('Error updating player status:', error);
            setError('Error updating player status');
        }
    };

    const handleAddEvent = async () => {
        if (!match) {
            setError('No match selected');
            return;
        }

        if (!newEventType || !newEventPlayer || !newEventPeriod) {
            setError('Please fill in all required fields');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const newEvent = {
                fk_period2match: parseInt(newEventPeriod),
                fk_event_type: parseInt(newEventType),
                fk_persona: parseInt(newEventPlayer),
                time: parseInt(newEventTime) || null,
                opt_add_time: newEventAddTime ? parseInt(newEventAddTime) : null,
                description: newEventDescription || null,
                fk_parent: newEventParent || null
            };

            const { data, error } = await supabase
                .from('match_events')
                .insert(newEvent)
                .select(`
                *,
                period2match!match_events_fk_period2match_fkey(
                    id,
                    fk_period(id, period)
                )
            `)
                .single();

            if (error) {
                console.error('Supabase error:', error);
                throw error;
            }

            console.log('New event added:', data);

            // Получаем полную информацию о периоде
            const { data: periodData, error: periodError } = await supabase
                .from('period2match')
                .select(`
                id,
                fk_period(id, period)
            `)
                .eq('id', newEventPeriod)
                .single();

            if (periodError) {
                console.error('Error fetching period data:', periodError);
                throw periodError;
            }

            // Создаем полное событие с информацией о периоде
            const fullEvent: MatchEvent = {
                ...data,
                period2match: periodData
            };

            // Обновляем состояние с полным событием
            setMatchEvents(prevEvents => [...prevEvents, fullEvent]);

            // Очистка полей формы
            setNewEventType('');
            setNewEventTime('');
            setNewEventPlayer('');
            setNewEventPeriod('');
            setNewEventAddTime('');
            setNewEventDescription('');
            setNewEventParent('');

            console.log('Event fields reset');
        } catch (err) {
            setError('Failed to add event. Please try again.');
            console.error('Error adding event:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddPeriod = async () => {
        if (!match || !selectedPeriodType) return;

        try {
            // Проверяем, не добавлен ли уже этот период к матчу
            const periodAlreadyAdded = matchPeriods.some(
                period => period.fk_period[0]?.id.toString() === selectedPeriodType
            );

            if (periodAlreadyAdded) {
                setError('This period has already been added to the match.');
                return;
            }

            // Добавляем новую запись в таблицу period2match
            const { data: newPeriod2Match, error: linkError } = await supabase
                .from('period2match')
                .insert({
                    fk_period: parseInt(selectedPeriodType),
                    fk_match: match.id
                })
                .select(`
        id,
        fk_period (
            id,
            period
        )
    `)
                .single();

            if (linkError) throw linkError;

// Приводим полученные данные к нужному типу
            const newPeriod: Period = {
                id: newPeriod2Match.id,
                fk_period: Array.isArray(newPeriod2Match.fk_period)
                    ? newPeriod2Match.fk_period
                    : [newPeriod2Match.fk_period]
            };

// Обновляем список периодов для текущего матча
            setMatchPeriods([...matchPeriods, newPeriod]);

            // Сбрасываем выбранный тип периода
            setSelectedPeriodType('');

            // Обновляем детали матча, чтобы отразить изменения
            fetchMatchDetails(match.id);
        } catch (err) {
            setError('Failed to add new period. Please try again.');
            console.error('Error adding new period:', err);
        }
    };

    const fetchMatchEvents = async (matchId: number): Promise<MatchEvent[]> => {
        try {
            const { data: periodsData } = await supabase
                .from('period2match')
                .select('id')
                .eq('fk_match', matchId);

            const periodIds = periodsData?.map(period => period.id) || [];

            if (periodIds.length === 0) {
                return [];
            }

            const { data: eventData, error: eventError } = await supabase
                .from('match_events')
                .select(`
                *,
                period2match!match_events_fk_period2match_fkey(
                    id,
                    fk_period(id, period)
                )
            `)
                .in('fk_period2match', periodIds)
                .order('time', { ascending: true });

            if (eventError) throw eventError;
            return eventData || [];
        } catch (err) {
            console.error('Error fetching match events:', err);
            throw err;
        }
    };

    const handleAddSubstitute = async (substituteData: SubstituteData) => {
        try {
            setIsLoading(true);
            setError(null);

            // Создаем событие "substitute out"
            const { data: outEvent, error: outError } = await supabase
                .from('match_events')
                .insert({
                    fk_period2match: substituteData.periodId,
                    fk_event_type: 4,
                    fk_persona: substituteData.playerOutId,
                    time: substituteData.time,
                    opt_add_time: substituteData.additionalTime,
                })
                .select()
                .single();

            if (outError) throw outError;
            if (!outEvent) throw new Error('Failed to create substitute out event');

            // Создаем событие "substitute in"
            const { data: inEvent, error: inError } = await supabase
                .from('match_events')
                .insert({
                    fk_period2match: substituteData.periodId,
                    fk_event_type: 3,
                    fk_persona: substituteData.playerInId,
                    time: substituteData.time,
                    opt_add_time: substituteData.additionalTime,
                    fk_parent: outEvent.id,
                })
                .select()
                .single();

            if (inError) throw inError;
            if (!inEvent) throw new Error('Failed to create substitute in event');

            // Получаем полную информацию о созданных событиях, включая данные о периоде
            const { data: fullEvents, error: fullEventsError } = await supabase
                .from('match_events')
                .select(`
                *,
                period2match!match_events_fk_period2match_fkey(
                    id,
                    fk_period(id, period)
                )
            `)
                .in('id', [outEvent.id, inEvent.id])
                .order('id');

            if (fullEventsError) throw fullEventsError;
            if (!fullEvents || fullEvents.length !== 2) throw new Error('Failed to fetch full event details');

            // Обновляем список событий
            setMatchEvents(prevEvents => [...prevEvents, ...fullEvents]);

        } catch (error) {
            console.error('Error adding substitution:', error);
            setError('Failed to add substitution. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;
    if (!match) return <div>Match not found</div>;

    return (
        <>
            <Header title={`Edit Match: ${match.name}`} menu={adminPageMenu} />
            <div className={styles.adminPageMatch}>
                <div className={styles.matchInfo}>
                    <h2>{match.name}</h2>
                    <p>Status: {match.status}</p>
                    <select
                        value={match.status}
                        onChange={(e) => handleUpdateMatchStatus(e.target.value)}
                    >
                        <option value="toSchedule">toSchedule</option>
                        <option value="Scheduled">Scheduled</option>
                        <option value="Postponed">Postponed</option>
                        <option value="Running">Running</option>
                        <option value="Played">Finished</option>
                        <option value="Officialised">Officialised</option>
                        <option value="Cancelled">Cancelled</option>
                    </select>
                    <p>Type: {match.type}</p>
                    <p>Start Time: {new Date(match.actual_start_time).toLocaleString()}</p>
                    <p>Home team: {match.fk_team_host} – {match.host_team_name?.name}</p>
                    <p>Guest team: {match.fk_team_guest} – {match.guest_team_name?.name}</p>
                </div>

                <div className={styles.teamSquads}>
                    <div className={styles.homeTeam}>
                        <h3>{match?.host_team_name?.name} Squad</h3>
                        <table>
                            <thead>
                            <tr>
                                <th>Name</th>
                                <th>In Squad</th>
                                <th>Starting</th>
                                <th>Captain</th>
                            </tr>
                            </thead>
                            <tbody>
                            {homeTeamPlayers.sort((a, b) => {
                                // Handle null or undefined numbers
                                if (a.number === null || a.number === undefined) return 1;
                                if (b.number === null || b.number === undefined) return -1;
                                return a.number - b.number;
                            }).map(player => (
                                <tr key={player.id}>
                                    <td>{player.number} {player.first_name} {player.last_name}</td>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={player.in_match_squad}
                                            onChange={() => updatePlayerStatus(player, 'home', 'in_match_squad')}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={player.from_start}
                                            onChange={() => updatePlayerStatus(player, 'home', 'from_start')}
                                            disabled={!player.in_match_squad}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={player.captain}
                                            onChange={() => updatePlayerStatus(player, 'home', 'captain')}
                                            disabled={!player.in_match_squad || !player.from_start}
                                        />
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                    <div className={styles.awayTeam}>
                        <h3>{match.guest_team_name?.name} {match.guest_team_name?.name} Squad</h3>
                        <table>
                            <thead>
                            <tr>
                                <th>Name</th>
                                <th>In Squad</th>
                                <th>Starting</th>
                                <th>Captain</th>
                            </tr>
                            </thead>
                            <tbody>
                            {awayTeamPlayers.sort((a, b) => {
                                // Handle null or undefined numbers
                                if (a.number === null || a.number === undefined) return 1;
                                if (b.number === null || b.number === undefined) return -1;
                                return a.number - b.number;
                            }).map(player => (
                                <tr key={player.id}>
                                    <td>{player.number} {player.first_name} {player.last_name}</td>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={player.in_match_squad}
                                            onChange={() => updatePlayerStatus(player, 'away', 'in_match_squad')}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={player.from_start}
                                            onChange={() => updatePlayerStatus(player, 'away', 'from_start')}
                                            disabled={!player.in_match_squad}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={player.captain}
                                            onChange={() => updatePlayerStatus(player, 'away', 'captain')}
                                            disabled={!player.in_match_squad || !player.from_start}
                                        />
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {matchEvents.map(event => {
                    const eventType = eventTypes.find(et => et.id === event.fk_event_type);
                    const player = [...homeTeamPlayers, ...awayTeamPlayers].find(p => p.id === event.fk_persona);
                    return (
                        <li key={event.id}>
                            {event.id} –
                            {event.period2match?.fk_period?.period && `${event.period2match.fk_period.period} –`}
                            {event.time !== null && `${event.time}'`}
                            {event.opt_add_time !== null && `+${event.opt_add_time}`}
                            {eventType && (
                                <>
                                    <img src={eventType.icon} alt={eventType.event_type} className={styles.eventIcon} />
                                    {eventType.event_type}
                                </>
                            )}
                            {player && ` by ${player.last_name}`}
                            {event.description && ` - ${event.description}`}
                        </li>
                    );
                })}

                <div className={styles.addPeriod}>
                    <h3>Add New Period</h3>
                    <select
                        value={selectedPeriodType}
                        onChange={(e) => setSelectedPeriodType(e.target.value)}
                        className={styles.selectField}
                    >
                        <option value="">Select period type</option>
                        {allPeriodTypes.map(periodType => (
                            <option key={periodType.id} value={periodType.id}>
                                {periodType.period}
                            </option>
                        ))}
                    </select>
                    <button
                        onClick={handleAddPeriod}
                        disabled={isLoading || !match || !selectedPeriodType}
                    >
                        Add Period
                    </button>
                </div>

                <h3>Add New Event</h3>
                <div className={styles.addEvent}>
                    <select value={newEventType} onChange={(e) => setNewEventType(e.target.value)}>
                        <option value="">Select event type</option>
                        {eventTypes.map(eventType => (
                            <option key={eventType.id} value={eventType.id}>{eventType.event_type}</option>
                        ))}
                    </select>
                    <select
                        value={newEventPeriod}
                        onChange={(e) => setNewEventPeriod(e.target.value)}
                        className={styles.selectField}
                    >
                        <option value="">Select period</option>
                        {matchPeriods.map(period => {
                            const periodName = period.fk_period[0]?.period || 'Unknown Period';
                            return (
                                <option key={period.id} value={period.id}>
                                    {period.id} – {periodName}
                                </option>
                            );
                        })}
                    </select>
                    <input
                        type="number"
                        placeholder="Time"
                        value={newEventTime}
                        onChange={(e) => setNewEventTime(e.target.value)}
                    />
                    <input
                        type="number"
                        placeholder="Additional Time"
                        value={newEventAddTime}
                        onChange={(e) => setNewEventAddTime(e.target.value)}
                    />
                    <select value={newEventPlayer} onChange={(e) => setNewEventPlayer(e.target.value)}>
                        <option value="">{match.host_team_name?.name} Players</option>
                        {[...homeTeamPlayers].map(player => (
                            <option key={player.id} value={player.id}>
                                {player.number} – {player.last_name}
                            </option>
                        ))}
                    </select>
                    <select value={newEventPlayer} onChange={(e) => setNewEventPlayer(e.target.value)}>
                        <option value="">{match.guest_team_name?.name} Players</option>
                        {[...awayTeamPlayers].map(player => (
                            <option key={player.id} value={player.id}>
                                {player.number} – {player.last_name}
                            </option>
                        ))}
                    </select>
                    <select value={newEventParent} onChange={(e) => setNewEventParent(e.target.value)}>
                        <option value="">Select parent event</option>
                        {matchEvents.map(event => (
                            <option key={event.id} value={event.id}>
                                {event.id}
                            </option>
                        ))}
                    </select>
                    <input
                        type="text"
                        placeholder="Description (optional)"
                        value={newEventDescription}
                        onChange={(e) => setNewEventDescription(e.target.value)}
                    />
                    <button onClick={handleAddEvent} disabled={isLoading}>
                        {isLoading ? 'Adding...' : 'Add Event'}
                    </button>
                </div>
                <h4>Add New Substitute</h4>
                    <SubstituteForm
                        periods={matchPeriods.map(period => ({
                            id: period.id,
                            name: period.fk_period[0]?.period || 'Unknown Period'
                        }))}
                        teams={[
                            { id: match?.fk_team_host, name: match?.host_team_name?.name },
                            { id: match?.fk_team_guest, name: match?.guest_team_name?.name }
                        ]}
                        players={[...homeTeamPlayers, ...awayTeamPlayers]}
                        onAddSubstitute={handleAddSubstitute}
                    />

            </div>
        </>
    );
};

export default AdminPageMatch;